import React, { useState, useContext } from 'react'
import { Card, Button, Input, Table, Alert, Spin, Typography } from 'antd'
import { AuthorizationContext } from '../../components/auth/AuthProvider'
import { useLanguage } from '../../components/context/LanguageContext'
import { apiRequest } from '../../helpers'

const { TextArea } = Input
const { Title } = Typography

const SQL = () => {
  const [query, setQuery] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { userData } = useContext(AuthorizationContext)
  const languageContext = useLanguage()

  // Check if user has access
  const hasAccess = userData?.email === 'vlad.lepadatu@tarainteractive.com'

  const executeQuery = async () => {
    if (!query.trim()) {
      setError(languageContext.t('SQL query cannot be empty') || 'SQL query cannot be empty')
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await apiRequest(
        `${process.env.REACT_APP_API}system/sql`,
        'POST',
        { query: query.trim() }
      )

      if (response?.ok) {
        setResult(response.data)
      } else {
        setError(response?.message || languageContext.t('Failed to execute query') || 'Failed to execute query')
      }
    } catch (err) {
      setError(err.message || languageContext.t('An error occurred while executing the query') || 'An error occurred while executing the query')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.ctrlKey && e.key === 'Enter') {
      executeQuery()
    }
  }

  if (!hasAccess) {
    return (
      <Card>
        <Alert
          message={languageContext.t('Access Denied') || 'Access Denied'}
          description={languageContext.t("You don't have permission to access this page.") || "You don't have permission to access this page."}
          type="error"
          showIcon
        />
      </Card>
    )
  }

  const columns = result?.columns?.map((col, index) => ({
    title: col,
    dataIndex: col,
    key: index,
    width: 150,
    fixed: col == 0 ? 'left' : null,
    ellipsis: true,
  })) || []

  const dataSource = result?.rows?.map((row, index) => {
    const record = { key: index }
    result.columns.forEach((col, colIndex) => {
      record[col] = row[colIndex]
    })
    return record
  }) || []

  return (
    <div style={{ padding: '24px' }}>
      <Card title={<Title level={3}>{languageContext.t('SQL Query Executor') || 'SQL Query Executor'}</Title>}>
        <div style={{ marginBottom: '16px' }}>
          <TextArea
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={languageContext.t('Enter your SQL query here... (Ctrl+Enter to execute)') || 'Enter your SQL query here... (Ctrl+Enter to execute)'}
            rows={6}
            onKeyDown={handleKeyPress}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Button
            type="primary"
            onClick={executeQuery}
            loading={loading}
            disabled={!query.trim()}
          >
            {languageContext.t('Execute Query (Ctrl+Enter)') || 'Execute Query (Ctrl+Enter)'}
          </Button>
        </div>

        {error && (
          <Alert
            message={languageContext.t('Error') || 'Error'}
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {loading && (
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <Spin size="large" />
          </div>
        )}

        {result && (
          <Card title={`${languageContext.t('Results') || 'Results'} (${result.rowCount || 0} ${languageContext.t('rows') || 'rows'})`} style={{ marginTop: '16px' }}>
            {result.rows && result.rows.length > 0 ? (
              <Table
                columns={columns}
                dataSource={dataSource}
                style={{ width: '1400px'}}
                scroll={{ x: true }}
                size="small"
                pagination={{
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                }}
              />
            ) : (
              <Alert
                message={languageContext.t('No data') || 'No data'}
                description={languageContext.t('Query executed successfully but returned no results.') || 'Query executed successfully but returned no results.'}
                type="info"
                showIcon
              />
            )}
          </Card>
        )}
      </Card>
    </div>
  )
}

export default SQL